import { useNavigate } from "react-router-dom";
import {
  Ticket,
  RefreshCw,
  BadgePercent,
  CheckCircle,
  DollarSign,
} from "lucide-react";
import { useBusinessDashboard } from "../../hooks/useBusinessDashboard";
import usePremiumStatus from "../../hooks/usePremiumStatus";
import DashboardContentWithOverlay from "../../components/Business/DashboardContentWithOverlay";

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const { dashboard, loading, refreshDashboard } = useBusinessDashboard();
  const { isPremium } = usePremiumStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Failed to load dashboard data</p>
          <button
            onClick={refreshDashboard}
            className="theme-button-primary px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Map API response structure to component expectations
  const businessInfo = {
    business_name: dashboard.business?.name || 'Business',
    is_premium: isPremium,
    subscription_package: dashboard.business?.subscription || 'Basic'
  };
  
  const transactionStats = {
    total_transactions: dashboard.statistics?.total_transactions || 0,
    fulfilled_transactions: dashboard.statistics?.total_transactions || 0, // Assuming all are fulfilled for now
    pending_transactions: 0, // Not provided in API response
    success_rate: dashboard.statistics?.total_transactions > 0 ? 100 : 0 // Assuming 100% success rate
  };
  
  const financialStats = {
    total_revenue: {
      formatted: `₹${dashboard.statistics?.total_revenue?.toLocaleString() || '0'}`
    },
    net_earnings: {
      formatted: `₹${dashboard.statistics?.net_earnings?.toLocaleString() || '0'}`
    },
    average_transaction_value: {
      formatted: `₹${dashboard.statistics?.average_transaction?.toLocaleString() || '0'}`
    },
    customer_savings: {
      formatted: `₹${dashboard.statistics?.customer_savings?.toLocaleString() || '0'}`
    },
    platform_commission: {
       formatted: `₹${dashboard.statistics?.platform_commission?.toLocaleString() || '0'}`
    }
  };
  
  const reportPeriod = {
    period_label: `${dashboard.period?.type || 'Month'} (${dashboard.period?.start_date || ''} to ${dashboard.period?.end_date || ''})`
  };

  const stats = [
    // {
    //   label: "Total Transactions",
    //   value: transactionStats.total_transactions,
    //   icon: Ticket,
    //   color: "blue",
    //   bgColor: "bg-blue-50",
    //   textColor: "text-blue-600",
    //   iconBg: "bg-blue-100",
    // },
    {
      label: "Completed",
      value: transactionStats.fulfilled_transactions,
      icon: CheckCircle,
      color: "green",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      iconBg: "bg-green-100",
    },
    // {
    //   label: "Pending",
    //   value: transactionStats.pending_transactions,
    //   icon: Clock,
    //   color: "yellow",
    //   bgColor: "bg-yellow-50",
    //   textColor: "text-yellow-600",
    //   iconBg: "bg-yellow-100",
    // },
    {
      label: "Total Revenue",
      value: financialStats.total_revenue.formatted,
      icon: DollarSign,
      color: "green",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      iconBg: "bg-green-100",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Welcome Section - Always visible */}
      <div className="theme-card rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {businessInfo.business_name}!
              </h1>
              {businessInfo.is_premium && (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                  {businessInfo.subscription_package}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={refreshDashboard}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            {businessInfo.is_premium ? (
              <button
                onClick={() => navigate("/business/transactions")}
                className="theme-button-gradient px-6 py-3 rounded-lg flex items-center space-x-2 hover:shadow-lg transition-all w-fit"
              >
                <Ticket className="h-5 w-5" />
                <span>New Transaction</span>
              </button>
            ) : (
              <button
                onClick={() => navigate("/business/discount")}
                className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-lg flex items-center space-x-2 hover:shadow-lg transition-all w-fit"
              >
                <BadgePercent className="h-5 w-5" />
                <span>Manage Discount</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Content with Conditional Overlay */}
      <DashboardContentWithOverlay 
        stats={stats}
        financialStats={financialStats}
        businessInfo={businessInfo}
        showOverlay={!businessInfo.is_premium}
      />
    </div>
  );
}
